import PageHeader from '@/components/layout/PageHeader';
import CtaBanner from '@/components/marketing/CtaBanner';
import Testimonials from '@/components/marketing/Testimonials';
import { ABOUT_IMAGE, COMPANY } from '@/data/site';
import { motion } from 'framer-motion';

const EASE = [0.16, 1, 0.3, 1];
const values = [
  { num: '01', k: 'Craft', v: 'Every corner. Every reveal. Every seam. It shows.' },
  { num: '02', k: 'Candor', v: 'Real numbers, real timelines, uncomfortable truths first.' },
  { num: '03', k: 'Care', v: 'Your home is your home. Boots off, floors covered, dust contained.' },
  { num: '04', k: 'Consistency', v: 'The same crew from demo day to punch list. No swaps.' },
];

export default function AboutPage() {
  return (
    <>
      <PageHeader
        chapter="Chapter 003"
        eyebrow="Studio · Established 2023"
        title={<>A studio of<br />builders, not marketers.</>}
        blurb="Alpha Tex started in 2023 with a small crew, a truck, and a rule: only take the work you'd be proud to walk clients through years later. Family-run, licensed, and locally rooted."
      />

      <section className="relative bg-[#FAFAF5] py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE }}
            className="md:col-span-6 relative aspect-[4/5] overflow-hidden clip-frame"
          >
            <img src={ABOUT_IMAGE} alt="Alpha Tex crew" className="w-full h-full object-cover" />
            <div className="absolute top-4 left-4 font-mono-label text-[10px] tracking-[0.24em] uppercase text-white bg-[#1D4ED8] px-3 py-1.5">
              Est. 2023
            </div>
            <div className="absolute inset-x-0 bottom-0 p-6 flex items-end justify-between bg-gradient-to-t from-black/80 to-transparent">
              <div className="font-mono-label text-[10px] tracking-[0.24em] uppercase text-white">Field team · San Antonio, TX</div>
              <div className="w-2 h-2 rounded-full bg-[#DC2626] animate-pulse" />
            </div>
          </motion.div>
          <div className="md:col-span-6">
            <div className="font-mono-label text-[10px] tracking-[0.28em] uppercase text-[#1D4ED8] mb-6">// Our story · Est. 2023</div>
            <h2 className="font-display uppercase text-[#0A0A0A] text-3xl md:text-5xl leading-[1.02] tracking-tight mb-8">
              We started small.<br />We stayed careful.
            </h2>
            <div className="space-y-5 text-[#0A0A0A]/70 leading-relaxed">
              <p>Alpha Tex was founded in 2023 as a full residential and commercial construction studio serving the {COMPANY.serviceArea}. Since day one we've delivered {COMPANY.projects}+ projects and turned down twice as many that weren't the right fit.</p>
              <p>We're the crew that shows up on Monday morning, tells you what's actually going on, and leaves the site cleaner than we found it. Sometimes that's boring. It's supposed to be. Construction should feel calm — because you should be planning your housewarming, not chasing your contractor.</p>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6">
              {[{k:COMPANY.years+'+',v:'Years'},{k:COMPANY.projects+'+',v:'Projects'},{k:COMPANY.crews,v:'Field crews'}].map((s) => (
                <div key={s.v}>
                  <div className="font-display text-[#0A0A0A] text-3xl md:text-4xl">{s.k}</div>
                  <div className="font-mono-label text-[10px] tracking-[0.22em] uppercase text-[#0A0A0A]/60 mt-2">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative bg-[#0A0A0A] py-24 md:py-32 border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="font-mono-label text-[10px] tracking-[0.28em] uppercase text-[#DC2626] mb-6">// Values</div>
          <h2 className="font-display uppercase text-white text-4xl md:text-6xl leading-[1.02] tracking-tight max-w-4xl mb-16">
            Four words the crew tapes to the truck.
          </h2>
          <div className="grid md:grid-cols-4 gap-0 border-l border-t border-white/10">
            {values.map((v) => (
              <div key={v.k} className="border-r border-b border-white/10 p-8">
                <div className="font-mono-label text-[10px] tracking-[0.24em] text-[#DC2626] mb-6">{v.num}</div>
                <h3 className="font-display uppercase text-white text-2xl md:text-3xl mb-3">{v.k}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{v.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-[#FAFAF5] py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="font-mono-label text-[10px] tracking-[0.28em] uppercase text-[#1D4ED8] mb-6">// Why Alpha Tex</div>
            <h2 className="font-display uppercase text-[#0A0A0A] text-4xl md:text-5xl leading-[1.02] tracking-tight">
              What makes us different in a directory full of contractors.
            </h2>
          </div>
          <div className="md:col-span-7 grid gap-6">
            {[
              ['Fixed pricing, itemized.', "You'll see line items. Change orders are always in writing before we start."],
              ['One project manager, direct line.', 'Named contact from estimate to punch list. Text them anytime.'],
              ['In-house tradespeople.', 'Framers, tile setters, cabinet makers — on payroll, not craigslist.'],
              ['Post-completion warranty.', 'Two-year workmanship warranty. We come back. We fix it. Free.'],
            ].map(([h, p]) => (
              <div key={h} className="border-l-2 border-[#1D4ED8] pl-6 py-1">
                <div className="font-display uppercase text-[#0A0A0A] text-xl md:text-2xl mb-2">{h}</div>
                <div className="text-[#0A0A0A]/70">{p}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />
      <CtaBanner place="about-page" />
    </>
  );
}
